import React, { useContext, useEffect } from 'react';
import { css } from '@compiled/react';
import { media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';
import { useLoaderData } from 'react-router-dom';

import { sendScreenEvent } from 'src/utils/analytics';
import { useAtlassianUser } from 'src/hooks/useAtlassianUser';
import { HelmetContext } from 'src/components/Helmet';
import { HeroSection } from 'src/pages/hubs/LearningPathPage/children/HeroSection';
import { ContentBlock } from 'src/pages/hubs/LearningPathPage/children/ContentBlock';
import { LearningPathsHubLoaderProps } from 'src/pages/hubs/LearningPathPage/loader';
import { getImageAttrs } from 'src/utils/contentful';
import { useReferrer } from 'src/hooks/useReferrer';

export const LearningPathPage: React.FC = () => {
  const { user, isLoading, hasError } = useAtlassianUser();
  const helmet = useContext(HelmetContext);
  const { referrerPath, isInitialized } = useReferrer();

  const { learningPathsHubData } = useLoaderData() as LearningPathsHubLoaderProps;

  helmet.setHelmet({
    title: `${learningPathsHubData.titleExternal} | Learning - Atlassian Community`,
    seoTags: learningPathsHubData.seo,
  });

  useEffect(() => {
    if (!isLoading && !hasError && isInitialized) {
      sendScreenEvent('learningPathsHubScreen', { learningReferrerPath: referrerPath }, user?.account_id);
    }
  }, [user, isLoading, hasError, referrerPath, isInitialized]);

  return (
    <main data-testid="learning-paths-hub" css={pageContainerStyles}>
      <HeroSection
        title={learningPathsHubData.hero.titleExternal}
        subtitle={learningPathsHubData.hero.subtitle}
        eyebrowText={learningPathsHubData.hero.eyebrowText}
      />
      <div data-testid="learning-paths-content-section" css={pageContentContainerStyles}>
        {!!learningPathsHubData.linkedContentBlockCollection.items.length &&
          learningPathsHubData.linkedContentBlockCollection.items.map((contentBlock) => (
            <ContentBlock
              key={contentBlock.sys.id}
              imageSrc={getImageAttrs(contentBlock.asset)?.src}
              title={contentBlock.titleExternal}
              subtitle={contentBlock.subtitle}
              associatedContent={contentBlock.associatedContentResourcesCollection.items}
            />
          ))}
      </div>
    </main>
  );
};

const pageContainerStyles = css({
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'column',
  alignItems: 'center',
  marginTop: '136px',

  '@media (max-width: 1279px)': {
    marginTop: '0',
  },
});

const pageContentContainerStyles = css({
  width: '100%',
  height: '100%',
  maxWidth: '1022px',
  display: 'flex',
  padding: `0 ${token('space.300')}`,
  flexDirection: 'column',
  alignItems: 'flex-start',

  [media.below.xs]: {
    padding: `0 ${token('space.200')}`,
  },
});
